import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CATEGORY_ORDER, CATEGORY_META, IMPACT_META, STATUS_META, CONFIDENCE_META, EVENT_TYPE_LABEL } from "@/lib/catalysts";
import { Lock } from "lucide-react";

const EMPTY = { title: "", date: "", category: "crypto", description: "", coins: "", impact: "neutral", sourceUrl: "", time: "", status: "scheduled", confidence: "confirmed", dateHint: "", eventType: "", parties: "", viral: false };

export default function CatalystForm({ open, onOpenChange, event, defaultDate, onSaved, privateMode = false, onSuggestion }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(event?.id);
  const isPrivate = privateMode || Boolean(event?.private);

  useEffect(() => {
    if (!open) return;
    if (event) {
      setForm({
        title: event.title || "",
        date: (event.date || "").slice(0, 10),
        category: event.category || "crypto",
        description: event.description || "",
        coins: (event.coins || []).join(", "),
        impact: event.impact || "neutral",
        sourceUrl: event.sourceUrl || "",
        time: event.time || "",
        status: event.status || "scheduled",
        confidence: event.confidence || "confirmed",
        dateHint: event.dateHint || "",
        eventType: event.eventType || "",
        parties: (event.parties || []).join(", "),
        viral: Boolean(event.viral),
      });
    } else {
      setForm({ ...EMPTY, date: defaultDate || "" });
    }
  }, [open, event, defaultDate]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || (!form.date && form.status !== "date_tbd")) {
      toast.error("Title and date are required (or set status to Date TBD)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        date: form.date,
        category: form.category,
        description: form.description.trim(),
        coins: form.coins.split(",").map((c) => c.trim()).filter(Boolean),
        impact: form.impact,
        sourceUrl: form.sourceUrl.trim(),
        time: form.time.trim(),
        status: form.status,
        confidence: form.confidence,
        dateHint: form.dateHint.trim(),
      };
      if (!isPrivate) {
        payload.eventType = form.eventType || "";
        payload.parties = form.parties.split(",").map((p) => p.trim()).filter(Boolean);
        payload.viral = form.viral;
      }
      let merged = false;
      if (isPrivate) {
        if (editing) await api.put(`/catalysts/mine/${event.id}`, payload);
        else {
          const { data } = await api.post("/catalysts/mine", payload);
          if (data.suggestion) onSuggestion?.(data.suggestion);
        }
      } else if (editing) await api.put(`/catalysts/${event.id}`, payload);
      else { const { data } = await api.post("/catalysts", payload); merged = data.merged; }
      toast.success(editing ? "Event updated" : merged ? "Matched an existing event — merged your details into it" : isPrivate ? "Private event added — only you can see it" : "Event added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-[#334566] max-w-lg" data-testid="catalyst-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? (isPrivate ? "Edit my event" : "Edit catalyst") : isPrivate ? "Add my event" : "Add catalyst"}</DialogTitle>
          <DialogDescription className="text-slate-400">{isPrivate ? "Private to your account — shows on your calendar and in your feed. Title and date are required." : editing ? "Anything you change here is locked — feeds and syncs won't overwrite it. Change the date and the event moves to that day." : "Mark an event that could move coins. Title and date are required."}</DialogDescription>
        </DialogHeader>
        {editing && !isPrivate && event?.adminLocked?.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-1.5" data-testid="catalyst-locked-note">
            <Lock className="h-3.5 w-3.5 shrink-0" /> Admin-locked: {event.adminLocked.join(", ")}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase">Title</label>
            <Input value={form.title} onChange={(e) => set("title")(e.target.value)}
              placeholder="e.g. US Election Day" className="bg-[#111723] border-[#334566]" data-testid="catalyst-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Date</label>
              <Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)}
                className="bg-[#111723] border-[#334566]" data-testid="catalyst-date-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Category</label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger className="bg-[#111723] border-[#334566]" data-testid="catalyst-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-[#334566]">
                  {CATEGORY_ORDER.map((c) => (
                    <SelectItem key={c} value={c}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_META[c].color }} />
                        {CATEGORY_META[c].label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase">Description</label>
            <Textarea value={form.description} onChange={(e) => set("description")(e.target.value)}
              placeholder="Why it could move the market…" rows={2} className="bg-[#111723] border-[#334566]" data-testid="catalyst-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Coins (comma sep)</label>
              <Input value={form.coins} onChange={(e) => set("coins")(e.target.value)}
                placeholder="BTC, SOL, DOGE" className="bg-[#111723] border-[#334566]" data-testid="catalyst-coins-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Impact</label>
              <Select value={form.impact} onValueChange={set("impact")}>
                <SelectTrigger className="bg-[#111723] border-[#334566]" data-testid="catalyst-impact-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-[#334566]">
                  {Object.entries(IMPACT_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />{v.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Time</label>
              <Input value={form.time} onChange={(e) => set("time")(e.target.value)} placeholder="8:30 AM ET" className="bg-[#111723] border-[#334566]" data-testid="catalyst-time-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Status</label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="bg-[#111723] border-[#334566]" data-testid="catalyst-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-[#334566]">
                  {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />{v.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Confidence</label>
              <Select value={form.confidence} onValueChange={set("confidence")}>
                <SelectTrigger className="bg-[#111723] border-[#334566]" data-testid="catalyst-confidence-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-[#334566]">
                  {Object.entries(CONFIDENCE_META).map(([k, v]) => <SelectItem key={k} value={k}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />{v.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.status === "date_tbd" && (
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Date hint</label>
              <Input value={form.dateHint} onChange={(e) => set("dateHint")(e.target.value)} placeholder="e.g. Q4 2026, later this month" className="bg-[#111723] border-[#334566]" data-testid="catalyst-datehint-input" />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase">Source URL (optional)</label>
            <Input value={form.sourceUrl} onChange={(e) => set("sourceUrl")(e.target.value)}
              placeholder="https://…" className="bg-[#111723] border-[#334566]" data-testid="catalyst-source-input" />
          </div>
          {!isPrivate && (
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-[#222F43]">
              <div>
                <label className="text-xs text-slate-400 font-mono uppercase">Event type</label>
                <Select value={form.eventType || "none"} onValueChange={(v) => set("eventType")(v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-[#111723] border-[#334566]" data-testid="catalyst-eventtype-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="glass border-[#334566]">
                    <SelectItem value="none">—</SelectItem>
                    {Object.entries(EVENT_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400 font-mono uppercase">Parties (comma sep)</label>
                <Input value={form.parties} onChange={(e) => set("parties")(e.target.value)} placeholder="SEC, Coinbase" className="bg-[#111723] border-[#334566]" data-testid="catalyst-parties-input" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
                <input type="checkbox" checked={form.viral} onChange={(e) => set("viral")(e.target.checked)} className="accent-orange-400 h-4 w-4" data-testid="catalyst-viral-checkbox" />
                Mark as viral (shows the flame and appears in everyone's feed)
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-[#334566] bg-transparent" onClick={() => onOpenChange(false)} data-testid="catalyst-cancel-btn">Cancel</Button>
          <Button className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" onClick={save} disabled={saving} data-testid="catalyst-save-btn">
            {saving ? "Saving…" : editing ? "Save changes" : "Add event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
